import { DocumentPhaseId } from '@blissbook/ui/application/graph'
import { faCircle } from '@fortawesome/pro-regular-svg-icons/faCircle'
import { faCircleHalfStroke } from '@fortawesome/pro-regular-svg-icons/faCircleHalfStroke'
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons/faCircleCheck'
import { faCircleXmark } from '@fortawesome/pro-solid-svg-icons/faCircleXmark'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import moment from 'moment'

type ReviewFeedback = {
  createdAt: string
  isApproved?: boolean
}

type Reviewer = {
  feedbacks: ReviewFeedback[]
}

type ReviewRound = {
  dueDate: string
  isFullApprovalRequired: boolean
  reviewers: Reviewer[]
}

export type DocumentPhase = {
  id: DocumentPhaseId
  icon: React.ReactNode
  label: string

  backgroundClassName?: string
  statusText?: string
  textClassName?: string
  timestampText?: (reviewRound: ReviewRound) => React.ReactNode
}

export const draftingPhase: DocumentPhase = {
  id: DocumentPhaseId.Drafting,
  icon: (
    <FontAwesomeIcon
      className='tw-text-gray-500'
      fixedWidth
      icon={faCircleHalfStroke}
    />
  ),
  label: 'Drafting',
}

export const inReviewPhase: DocumentPhase = {
  id: DocumentPhaseId.InReview,
  icon: (
    <FontAwesomeIcon
      className='tw-text-sunshine-700 tw-rotate-180'
      fixedWidth
      icon={faCircleHalfStroke}
    />
  ),
  label: 'In Review',
  backgroundClassName: 'tw-bg-sunshine-100',
  textClassName: 'tw-text-sunshine-800',
  timestampText: (reviewRound) =>
    `Due ${moment(reviewRound.dueDate).format('MMM D, YYYY')}`,
}

export const approvedPhase: DocumentPhase = {
  id: DocumentPhaseId.Approved,
  icon: (
    <FontAwesomeIcon
      className='tw-text-green-700'
      fixedWidth
      icon={faCircleCheck}
    />
  ),
  label: 'Approved',
  backgroundClassName: 'tw-bg-green-50',
  textClassName: 'tw-text-green-800',
  statusText: 'This version has been approved!',
  timestampText: (reviewRound) =>
    `Latest approval ${getApprovedAt(reviewRound).format('MMM D, YYYY')}`,
}

export const rejectedPhase: DocumentPhase = {
  id: DocumentPhaseId.Rejected,
  icon: (
    <FontAwesomeIcon
      className='tw-text-red-700'
      fixedWidth
      icon={faCircleXmark}
    />
  ),
  label: 'Needs Revision',
  backgroundClassName: 'tw-bg-red-50',
  textClassName: 'tw-text-red-800',
  statusText: 'This version needs revision.',
  timestampText: (reviewRound) =>
    `Changes requested ${getRejectedAt(reviewRound).format('MMM D, YYYY')}`,
}

export const noDecisionPhase: DocumentPhase = {
  id: DocumentPhaseId.NoDecision,
  icon: (
    <FontAwesomeIcon
      className='tw-text-gray-500'
      fixedWidth
      icon={faCircleXmark}
    />
  ),
  label: 'No Decision',
  backgroundClassName: 'tw-bg-gray-200',
  textClassName: 'tw-text-gray-700',
  statusText: 'This version received feedback without an approval decision.',
  timestampText: (reviewRound) =>
    `Due date passed ${moment(reviewRound.dueDate).format('MMM D, YYYY')}`,
}

export const cleanPhase: DocumentPhase = {
  id: DocumentPhaseId.Clean,
  icon: (
    <FontAwesomeIcon className='tw-text-gray-500' fixedWidth icon={faCircle} />
  ),
  label: 'Clean',
}

export const documentPhases = [
  draftingPhase,
  inReviewPhase,
  approvedPhase,
  rejectedPhase,
  noDecisionPhase,
  cleanPhase,
]

export const documentPhasesById = new Map(
  documentPhases.map((phase) => [phase.id, phase]),
)

export type DocumentPhaseDocument = {
  activeReviewRound?: {
    dueDate: string
    initialHtmlHash?: number | null
  }
  draftVersion: { htmlHash: number }
  phaseId: DocumentPhaseId
}

export function hasDocumentReviewRoundEnded(dueDate: string) {
  return moment(dueDate).endOf('day').isBefore(new Date())
}

export function getDocumentPhase(document: DocumentPhaseDocument) {
  const { activeReviewRound, phaseId } = document

  if (
    phaseId === DocumentPhaseId.InReview &&
    activeReviewRound &&
    hasDocumentReviewRoundEnded(activeReviewRound.dueDate)
  ) {
    return noDecisionPhase
  }

  return documentPhasesById.get(phaseId)
}

export function getDocumentsPhases(documents: DocumentPhaseDocument[]) {
  const phases = new Set<DocumentPhase>()
  for (const document of documents) {
    const phase = getDocumentPhase(document)
    phases.add(phase)
  }
  return Array.from(phases)
}

export function getDocumentRoundPhase(reviewRound: {
  isFullApprovalRequired: boolean
  reviewers: {
    feedbacks: { isApproved?: boolean }[]
  }[]
}) {
  const { isFullApprovalRequired, reviewers } = reviewRound

  // Count the number of approvals
  let approvedCount = 0
  for (const reviewer of reviewers) {
    const lastFeedback = reviewer.feedbacks.at(-1)
    if (!lastFeedback) continue
    if (lastFeedback.isApproved === false) return DocumentPhaseId.Rejected
    if (lastFeedback.isApproved === true) approvedCount++
  }

  const isApproved = isFullApprovalRequired
    ? approvedCount === reviewers.length
    : approvedCount >= 1
  return isApproved ? DocumentPhaseId.Approved : DocumentPhaseId.InReview
}

/** Get the latest feedback for each reviewer */
function getLatestFeedbacks(reviewRound: ReviewRound) {
  return reviewRound.reviewers
    .map((reviewer) => reviewer.feedbacks.at(-1))
    .filter(Boolean)
}

/** Get the date of the first rejection */
function getRejectedAt(reviewRound: ReviewRound) {
  const lastestFeedbacks = getLatestFeedbacks(reviewRound)
  const rejectedAts = lastestFeedbacks
    .filter((feedback) => feedback.isApproved === false)
    .map((feedback) => moment(feedback.createdAt))
  return moment.min(rejectedAts)
}

/** If approved, return the first if any, last if all */
function getApprovedAt(reviewRound: ReviewRound) {
  const { isFullApprovalRequired } = reviewRound
  const lastestFeedbacks = getLatestFeedbacks(reviewRound)

  const approvedAts = lastestFeedbacks
    .filter((feedback) => feedback.isApproved === true)
    .map((feedback) => moment(feedback.createdAt))

  return isFullApprovalRequired
    ? moment.max(approvedAts)
    : moment.min(approvedAts)
}
